/**
 * Product Grid block — scoped AJAX filter driver.
 *
 * One instance per [data-txpg] block (multiple blocks per page are
 * fine). Two optional filter surfaces, either or both:
 *   • the sidebar rail — the same shared partial /shop/ renders
 *     ([data-txpg-form], shop-filter-rail.php)
 *   • the category chip bar above the grid ([data-txpg-cat-rail],
 *     shop-style buttons that filter in place)
 *
 * Scope semantics — the backend filters are a SOFT DEFAULT: they
 * render pre-checked / pre-active, but the visitor's live state is
 * the filter. With a sidebar, the form is the single source of truth
 * and chips are shortcuts that rewrite the cat[] checkboxes. Without
 * a sidebar, the backend defaults (data-txpg-locked.defaults) carry
 * the non-category axes and the active chip carries the category —
 * as they do for any axis whose group Theme Options leaves out of
 * the sidebar.
 *   • sort / per_page / cols always come from the block config
 *   • CLEAR ALL is a native form reset — back to the editor's
 *     configured defaults, not an empty rail
 *
 * Fetches the same tx-sensors/v1/shop endpoint the shop page uses and
 * swaps grid_html in place. Deliberately NO history.pushState — the
 * block lives inside an arbitrary page whose URL it must not rewrite
 * (that's the shop page's UX, driven separately by shop-ajax.js).
 *
 * No-JS fallback: the SSR grid simply stays as rendered.
 */
const COUNT_MAPS = {
    cat:      'cat_counts',
    brand:    'brand_counts',
    ptag:     'tag_counts',
    thread:   'thread_counts',
    ip:       'ip_counts',
    material: 'material_counts',
};

const initBlock = (app) => {
    const endpoint = app.dataset.txpgRest;
    const grid     = app.querySelector('[data-txpg-grid]');
    if (!endpoint || !grid) return;

    let locked = {};
    try {
        locked = JSON.parse(app.dataset.txpgLocked || '{}');
    } catch {
        return;
    }
    const defaults = locked.defaults || {};

    const form    = app.querySelector('[data-txpg-form]');
    const chipBar = app.querySelector('[data-txpg-cat-rail]');
    if (!form && !chipBar) return;

    const chips    = chipBar ? [...chipBar.querySelectorAll('[data-txpg-cat]')] : [];
    const countEl  = app.querySelector('[data-txpg-count]');
    let inflight   = null;
    let lastHadQ   = false;
    // Rail-less category state: undefined = untouched (use defaults),
    // '' = All categories, 'slug' = that category.
    let chipCat;

    const checkedValues = (name) => form
        ? [...form.querySelectorAll(`input[name="${name}[]"]:checked`)].map((i) => i.value)
        : [];
    // Theme Options → Shop filters can leave a group out of the rail;
    // that axis then keeps the backend scope, as on a rail-less block.
    const inRail = (name) => !!form && !!form.querySelector(`input[name="${name}"]`);

    const currentCats = () => {
        if (inRail('cat[]')) return checkedValues('cat');
        if (chipCat !== undefined) return chipCat === '' ? [] : [chipCat];
        return defaults.cat || [];
    };

    const buildParams = () => {
        const p = new URLSearchParams();
        if (locked.sort)     p.set('sort', locked.sort);
        if (locked.per_page) p.set('per_page', locked.per_page);
        if (locked.cols)     p.set('cols', locked.cols);
        // The block's own sidebar list (signed), when it has one.
        if (locked.rail)     p.set('rail', locked.rail);

        currentCats().forEach((v) => p.append('cat[]', v));

        if (form) {
            // The rail's live state is the filter — the backend scope
            // only pre-checks the initial render (soft default).
            for (const axis of ['brand', 'ptag', 'thread', 'ip', 'material']) {
                const values = inRail(`${axis}[]`) ? checkedValues(axis) : (defaults[axis] || []);
                values.forEach((v) => p.append(`${axis}[]`, v));
            }

            const q = form.querySelector('input[name="q"]');
            if (q && q.value.trim() !== '') p.set('q', q.value.trim());

            const priceWrap = form.querySelector('[data-shop-price-range]');
            if (priceWrap) {
                const floor   = parseInt(priceWrap.dataset.priceFloor, 10);
                const ceiling = parseInt(priceWrap.dataset.priceCeiling, 10);
                const mn      = priceWrap.querySelector('[data-shop-price-min]');
                const mx      = priceWrap.querySelector('[data-shop-price-max]');
                if (mn && parseInt(mn.value, 10) > floor)   p.set('price_min', mn.value);
                if (mx && parseInt(mx.value, 10) < ceiling) p.set('price_max', mx.value);
            }

            const stockCb = form.querySelector('input[name="in_stock"]');
            if (stockCb ? stockCb.checked : defaults.in_stock) p.set('in_stock', '1');

            // Theme Options → Shop filters: product attributes and the
            // pressure range (its value inputs are disabled when unset).
            form.querySelectorAll('input[type="checkbox"][name^="attr["]:checked').forEach((cb) => {
                p.append(cb.name, cb.value);
            });
            for (const name of ['prmin', 'prmax']) {
                const input = form.querySelector(`input[name="${name}"]`);
                if (input && !input.disabled && input.value !== '') p.set(name, input.value);
            }
        } else {
            // Chips-only block: the non-category axes ride the backend
            // defaults unchanged.
            for (const axis of ['brand', 'ptag']) {
                (defaults[axis] || []).forEach((v) => p.append(`${axis}[]`, v));
            }
            if (defaults.in_stock) p.set('in_stock', '1');
        }
        return p;
    };

    const syncChips = () => {
        if (!chips.length) return;
        const cats   = currentCats();
        const single = cats.length === 1 ? cats[0] : (cats.length === 0 ? '' : null);
        chips.forEach((chip) => {
            const active = single !== null && chip.dataset.txpgCat === single;
            chip.classList.toggle('is-active', active);
            chip.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
    };

    const refresh = async () => {
        if (inflight) inflight.abort();
        const ctrl = new AbortController();
        inflight = ctrl;
        app.classList.add('is-loading');
        try {
            const params = buildParams();
            lastHadQ = params.has('q');
            const res = await fetch(`${endpoint}?${params}`, {
                signal:  ctrl.signal,
                headers: { Accept: 'application/json' },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();

            grid.innerHTML = data.grid_html;

            if (countEl) {
                countEl.textContent = `${data.filtered_count} OF ${countEl.dataset.txpgTotal} SKUs`;
            }
            app.querySelectorAll('[data-shop-count]').forEach((el) => {
                const key = el.dataset.shopCount;
                if (key === 'stock') {
                    el.textContent = data.in_stock_count;
                    return;
                }
                if (key.startsWith('attr:')) {
                    // attr:<slug>:<value> — the value may itself hold colons,
                    // and is free text: own keys only.
                    const own  = (o, k) => !!o && Object.prototype.hasOwnProperty.call(o, k);
                    const rest = key.slice(5);
                    const at   = rest.indexOf(':');
                    const slug = rest.slice(0, at);
                    const value = rest.slice(at + 1);
                    if (at > 0 && own(data.attr_counts, slug) && own(data.attr_counts[slug], value)) {
                        el.textContent = data.attr_counts[slug][value];
                    }
                    return;
                }
                if (key.startsWith('prpreset:')) {
                    const n = (data.pressure_preset_counts || [])[Number(key.slice(9))];
                    if (n != null) {
                        el.textContent = n;
                        // An empty quick range can't be picked (a pressed one stays usable).
                        const button = el.closest('[data-shop-pressure-preset]');
                        if (button && document.activeElement !== button) {
                            button.disabled = n === 0 && button.getAttribute('aria-pressed') !== 'true';
                        }
                    }
                    return;
                }
                const sep = key.indexOf(':');
                if (sep === -1) return;
                const map = data[COUNT_MAPS[key.slice(0, sep)]];
                const slug = key.slice(sep + 1);
                if (map && map[slug] != null) el.textContent = map[slug];
            });
        } catch (err) {
            if (err.name !== 'AbortError') {
                // Leave the last-rendered grid in place; a transient
                // REST failure shouldn't blank the section.
                console.error('tx-product-grid:', err); // eslint-disable-line no-console
            }
        } finally {
            // An aborted refresh settles AFTER the one that replaced it
            // — only the still-current controller may clear shared state,
            // or the stale finally un-dims the grid and orphans the live
            // request's controller (letting responses race).
            if (inflight === ctrl) {
                app.classList.remove('is-loading');
                inflight = null;
            }
        }
    };

    // Checkbox flips and slider commits can arrive as both a native
    // 'change' and a synthetic 'shop:filter-change' — coalesce every
    // trigger within a frame into one fetch.
    let scheduled = false;
    const scheduleRefresh = () => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
            scheduled = false;
            refresh();
        });
    };

    chips.forEach((chip) => {
        chip.addEventListener('click', () => {
            const slug = chip.dataset.txpgCat;
            if (inRail('cat[]')) {
                form.querySelectorAll('input[name="cat[]"]').forEach((cb) => {
                    cb.checked = cb.value === slug;
                });
            } else {
                chipCat = slug;
            }
            syncChips();
            scheduleRefresh();
        });
    });

    if (form) {
        form.addEventListener('change', (e) => {
            const t = e.target;
            if (!t || t.disabled) return;
            if (t.type === 'checkbox' || t.type === 'range') {
                syncChips();
                scheduleRefresh();
            }
        });
        form.addEventListener('submit', (e) => {
            e.preventDefault();
            scheduleRefresh();
        });
        form.addEventListener('shop:filter-change', scheduleRefresh);
        form.addEventListener('shop:search-cleared', () => {
            if (lastHadQ) scheduleRefresh();
        });
        // A Back / Forward that misses the bfcache re-renders the block's
        // backend state, and the browser then restores the rail's
        // controls over it. The block keeps no URL to read them back
        // from: return to the backend state.
        window.addEventListener('pageshow', (e) => {
            if (!e.persisted && performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
                form.reset();
            }
        });
        form.addEventListener('reset', () => {
            // Let the native reset restore the SSR defaults first, then
            // repaint the slider (it listens for shop:state-restored) and
            // refetch the configured default state.
            setTimeout(() => {
                chipCat = undefined;
                document.dispatchEvent(new CustomEvent('shop:state-restored'));
                syncChips();
                scheduleRefresh();
            }, 0);
        });
    }
};

const init = () => {
    document.querySelectorAll('[data-txpg]').forEach(initBlock);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
